import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Head from 'next/head';
import { AppBar, Box, Button, Drawer, IconButton, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsIcon from '@mui/icons-material/SettingsTwoTone';
import Link from 'components/common/Link';
import { useRouter } from 'next/router';
import { useSession } from 'components/SessionContext';
import AccountStatusAlerts from 'components/common/AccountStatusAlerts';
import Loader from 'components/common/Loader';
import ResetPasswordForm from 'components/user/ResetPasswordForm';
import OnboardingRobot from 'components/onboarding/OnboardingRobot';
import type { BreadCrumb } from 'components/AuthLayout';
import SettingsHomeContent from './SettingsHomeContent';
import { EXPANDED_SIDEBAR_WIDTH_PX } from './settingsHomeNavItems';
import { getSettingsBrowserTitle } from './utils';

const SETTINGS_SIDEBAR_SCROLL_KEY = 'harbor-settings-sidebar-scroll-y';
const EXIT_SETTINGS_RETURN_TO_KEY = 'harbor-settings-exit-return-to';

type SettingsShellProps = {
  children: React.ReactNode;
  breadCrumb?: BreadCrumb;
  isLoading?: boolean;
  testId?: string;
};

/**
 * Settings-only layout: no main app drawer. Settings index stays on the left; children on the right.
 * Below md the nav is a temporary overlay drawer; md+ keeps the permanent 210px sidebar.
 */
const SettingsShell = ({ children, breadCrumb, isLoading, testId }: SettingsShellProps) => {
  const theme = useTheme();
  const router = useRouter();
  const { user, shouldHideInfoSections, isTrialExpired } = useSession();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    const returnToQuery = router.query.returnTo;
    const returnTo = Array.isArray(returnToQuery) ? returnToQuery[0] : returnToQuery;
    if (!returnTo || typeof returnTo !== 'string') return;

    try {
      sessionStorage.setItem(EXIT_SETTINGS_RETURN_TO_KEY, returnTo);
    } catch {
      // Ignore storage errors (e.g. blocked cookies / privacy mode).
    }
  }, [router.query.returnTo]);

  const restoreSidebarScroll = useCallback(() => {
    const el = sidebarRef.current;
    if (!el || typeof window === 'undefined') return;
    const raw = sessionStorage.getItem(SETTINGS_SIDEBAR_SCROLL_KEY);
    if (raw == null) return;
    const y = Number(raw);
    if (!Number.isFinite(y) || y < 0) return;
    el.scrollTop = y;
  }, []);

  const persistSidebarScroll = useCallback(() => {
    const el = sidebarRef.current;
    if (!el) return;
    sessionStorage.setItem(SETTINGS_SIDEBAR_SCROLL_KEY, String(el.scrollTop));
  }, []);

  useLayoutEffect(() => {
    restoreSidebarScroll();
  }, [router.asPath, restoreSidebarScroll]);

  useEffect(() => {
    const onComplete = () => {
      setMobileOpen(false);
      requestAnimationFrame(() => restoreSidebarScroll());
    };
    router.events.on('routeChangeComplete', onComplete);
    return () => {
      router.events.off('routeChangeComplete', onComplete);
    };
  }, [router.events, restoreSidebarScroll]);

  useEffect(() => {
    if (user?.isResetPasswordRequired) {
      setShowResetPassword(true);
    }
  }, [user?.isResetPasswordRequired]);

  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(open => !open);
    }
  };

  const handleExitSettings = useCallback(() => {
    const fallback = '/';

    const returnToQuery = router.query.returnTo;
    const returnToFromQuery = Array.isArray(returnToQuery) ? returnToQuery[0] : returnToQuery;

    let returnUrl: string | null = null;
    if (typeof returnToFromQuery === 'string' && returnToFromQuery) {
      returnUrl = returnToFromQuery;
    } else {
      try {
        returnUrl = sessionStorage.getItem(EXIT_SETTINGS_RETURN_TO_KEY);
      } catch {
        returnUrl = null;
      }
    }

    if (!returnUrl || typeof returnUrl !== 'string') {
      void router.push(fallback);
      return;
    }

    if (returnUrl.startsWith('/settings')) {
      void router.push(fallback);
      return;
    }

    if (!returnUrl.startsWith('/')) {
      void router.push(fallback);
      return;
    }

    void router.push(returnUrl);
  }, [router]);

  const renderBreadCrumb = () => {
    if (!breadCrumb) {
      return null;
    }

    const containerStyle = {
      gap: '4px',
      alignItems: 'center',
      minWidth: 0,
      fontSize: { xs: '12px', sm: '12px', md: '13px' },
      display: 'flex',
    };

    const linkStyle = {
      textDecoration: 'none',
      color: theme.palette.text.secondary,
      ':hover': {
        color: theme.palette.text.primary,
      },
      ':visited': {
        color: theme.palette.text.secondary,
      },
      transition: 'all 0.2s ease',
      display: { xs: 'none', md: 'flex' },
    };

    const separatorStyle = {
      margin: '0 3px',
      color: theme.palette.text.disabled,
      fontSize: '0.75rem',
    };

    const titleStyle = {
      color: theme.palette.text.primary,
    };

    return (
      <Box sx={containerStyle}>
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>
          <SettingsIcon sx={{ fontSize: '1.1rem', color: 'text.secondary', mr: 0.5 }} />
        </Box>

        {breadCrumb.parent && (
          <>
            <Link href={breadCrumb.parent.link} sx={linkStyle}>
              {breadCrumb.parent.title}
            </Link>
            <Box component="span" sx={{ ...separatorStyle, display: { xs: 'none', md: 'inherit' } }}>
              ›
            </Box>
          </>
        )}

        {breadCrumb.subParent && (
          <>
            <Link href={breadCrumb.subParent.link} sx={linkStyle}>
              {breadCrumb.subParent.title}
            </Link>
            <Box component="span" sx={{ ...separatorStyle, display: { xs: 'none', md: 'inherit' } }}>
              ›
            </Box>
          </>
        )}

        <Box
          component="span"
          sx={{ ...titleStyle, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {breadCrumb.title}
        </Box>

        <AccountStatusAlerts user={user} isTrialExpired={isTrialExpired} />
      </Box>
    );
  };

  if (!user) {
    return null;
  }

  const browserTitle = getSettingsBrowserTitle(breadCrumb);

  const sidebarSurfaceSx = {
    backgroundColor: theme.palette.secondary.main,
    backgroundImage: 'none',
    color: theme.palette.secondary.contrastText,
    width: EXPANDED_SIDEBAR_WIDTH_PX,
    overflowY: 'auto' as const,
    overflowX: 'hidden' as const,
    height: '100%',
    pr: 0.5,
    pl: 0.75,
    pb: '20px',
    boxSizing: 'border-box' as const,
    borderRight: `1px solid ${theme.palette.divider}`,
    borderRadius: 0,
  };

  const renderSidebarNav = () => (
    <>
      <Button
        color="secondary"
        fullWidth
        variant="text"
        onClick={handleExitSettings}
        data-testid="@settings-exit"
        startIcon={<CloseIcon sx={{ fontSize: '1rem !important' }} />}
        sx={t => ({
          position: 'sticky',
          top: 0,
          pt: 1,
          pb: 1,
          zIndex: 1,
          justifyContent: 'flex-start',
          color: t.palette.secondary.contrastText,
          backgroundColor: t.palette.secondary.main,
          height: '32px',
          mt: 1,
          mb: 1,
          width: '100%',
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: t.shadows[1],
          '&:hover': {
            backgroundColor: t.palette.primary.main,
          },
        })}
      >
        Exit settings
      </Button>
      <SettingsHomeContent />
    </>
  );

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
      data-testid={testId}
    >
      {browserTitle ? (
        <Head>
          <title>{browserTitle}</title>
          {breadCrumb?.titleText ? <meta name="description" content={breadCrumb.titleText} /> : null}
        </Head>
      ) : null}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onTransitionEnd={handleDrawerTransitionEnd}
          onClose={handleDrawerClose}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              backgroundColor: theme.palette.secondary.main,
              backgroundImage: 'none',
              color: theme.palette.secondary.contrastText,
            },
          }}
          slotProps={{
            root: {
              keepMounted: true,
            },
          }}
          PaperProps={{
            sx: sidebarSurfaceSx,
          }}
        >
          {renderSidebarNav()}
        </Drawer>

        <Box
          ref={sidebarRef}
          onScroll={persistSidebarScroll}
          sx={{
            display: { xs: 'none', md: 'block' },
            flexShrink: 0,
            ...sidebarSurfaceSx,
          }}
        >
          {renderSidebarNav()}
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <AppBar
            color="secondary"
            position="static"
            elevation={0}
            sx={t => ({
              color: t.palette.text.primary,
              backgroundColor: t.palette.mode === 'dark' ? t.palette.secondary.main : t.palette.background.paper,
              borderBottom: `1px solid ${t.palette.divider}`,
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              padding: { xs: '8px 12px', sm: '8px 24px' },
              alignItems: 'center',
              flexShrink: 0,
              width: '100%',
              zIndex: t.zIndex.drawer + 1,
              borderRadius: 0,
              minHeight: '48px',
              height: 'auto',
            })}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <IconButton
                aria-label="Open settings menu"
                color="inherit"
                onClick={handleDrawerToggle}
                sx={{ display: { xs: 'inline-flex', md: 'none' }, flexShrink: 0 }}
              >
                <MenuIcon fontSize="inherit" />
              </IconButton>
              {renderBreadCrumb()}
            </Box>

            <Button
              variant="text"
              color="inherit"
              onClick={handleExitSettings}
              data-testid="@settings-exit-mobile"
              startIcon={<CloseIcon sx={{ fontSize: '1rem !important' }} />}
              sx={{
                display: { xs: 'inline-flex', md: 'none' },
                flexShrink: 0,
                textTransform: 'none',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
            >
              Exit
            </Button>
          </AppBar>

          {isLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: { xs: 0, md: `${EXPANDED_SIDEBAR_WIDTH_PX}px` },
                right: 0,
                zIndex: 1000,
              }}
            >
              <Loader />
            </Box>
          )}

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              width: '100%',
              py: 2,
              px: { xs: 2, sm: 3 },
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={t => ({
                width: { xs: '100%', sm: '90%', md: '80%' },
                maxWidth: t.breakpoints.values.lg,
                mx: 'auto',
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
              })}
            >
              {children}
            </Box>
          </Box>
        </Box>
      </Box>

      {showResetPassword && <ResetPasswordForm onSuccess={() => setShowResetPassword(false)} />}
      {!shouldHideInfoSections() && <OnboardingRobot />}
    </Box>
  );
};

export default SettingsShell;
